import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Send, BadgeCheck, AlertCircle, PartyPopper, Paperclip, X, Check, ListTodo, Clock, Trash2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const API_URL = `${window.location.origin}/api`;
const STATUS_CHIP = {
  assignée: "bg-sky-50 text-sky-700",
  traitée: "bg-amber-50 text-amber-700",
};

function TaskCard({ t, children }) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid={`task-card-${t.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-semibold leading-snug text-korban-navy">{t.title}</h3>
        </div>
        <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-bold ${STATUS_CHIP[t.status] || "bg-slate-100 text-slate-600"}`} data-testid={`task-status-${t.id}`}>
          {t.status}
        </span>
      </div>
      {t.description && <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{t.description}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold">Assignée par {t.assigner_name}</span>
        <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold">Pour {t.assignee_name}</span>
        <span className="rounded bg-slate-100 px-2 py-0.5 font-semibold">{(t.created_at || "").slice(0, 10)}</span>
      </div>
      {t.treated_note && (
        <p className="mt-2.5 rounded-lg bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">
          <b className="text-korban-navy">Traitement de l'agent :</b> {t.treated_note}
        </p>
      )}
      {t.attachment_path && (
        <a
          href={`${API_URL}/files/${t.attachment_path}`}
          target="_blank"
          rel="noopener"
          data-testid={`task-attachment-${t.id}`}
          className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-korban-blue/10 px-3 py-1.5 text-xs font-bold text-korban-navy2 transition-colors hover:bg-korban-blue/20"
        >
          <Paperclip size={13} /> {t.attachment_name || "Pièce jointe"}
        </a>
      )}
      {t.review === "parfait" && (
        <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid={`task-satisfaction-${t.id}`}>
          <PartyPopper size={18} className="mt-0.5 shrink-0 text-emerald-600" />
          <p className="text-sm font-semibold leading-relaxed text-emerald-700">
            Félicitations ! {t.reviewer_name} juge votre travail <b>parfait</b>. Continuez ainsi.
          </p>
        </div>
      )}
      {t.review === "corriger" && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4" data-testid={`task-corriger-${t.id}`}>
          <p className="flex items-start gap-2 text-sm font-bold text-red-600">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> À corriger — commentaire de {t.reviewer_name} :
          </p>
          <p className="mt-2 text-sm leading-relaxed text-red-700">{t.review_comment}</p>
        </div>
      )}
      {children}
    </article>
  );
}

export default function StaffTasks({ user }) {
  const [tasks, setTasks] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [treatId, setTreatId] = useState(null);
  const [treatNote, setTreatNote] = useState("");
  const [treatBusy, setTreatBusy] = useState(false);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_ids: [] });
  const fileRef = useRef(null);

  const toggleAssignee = (id) => {
    setForm((f) => ({
      ...f,
      assignee_ids: f.assignee_ids.includes(id)
        ? f.assignee_ids.filter((x) => x !== id)
        : [...f.assignee_ids, id],
    }));
  };

  const assignTask = async (e) => {
    e.preventDefault();
    if (form.assignee_ids.length === 0) return toast.error("Choisissez au moins un agent destinataire.");
    setBusy(true);
    try {
      const { data } = await api.post("/tasks", {
        title: form.title,
        description: form.description,
        assignee_ids: form.assignee_ids,
      });
      toast.success(`Tâche assignée à ${data.length} agent${data.length > 1 ? "s" : ""}.`);
      setForm({ title: "", description: "", assignee_ids: [] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const load = () => {
    api.get("/tasks").then((r) => setTasks(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/profiles").then((r) => setProfiles(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const isManager = user.role === "responsable" || user.role === "gerant";
  const received = (tasks || []).filter((t) => t.assignee_id === user.user_id);
  const mine = (tasks || []).filter((t) => t.assigner_id === user.user_id);
  const toTreat = received.filter((t) => ["assignée", "corriger"].includes(t.status)).length;
  const toReview = mine.filter((t) => t.status === "traitée").length;
  const candidates = profiles.filter((p) => p.active && p.role !== "gerant");

  const treatTask = async (t) => {
    setTreatBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", treatNote);
      const f = fileRef.current?.files?.[0];
      if (f) fd.append("file", f);
      await api.post(`/tasks/${t.id}/treat`, fd);
      toast.success("Tâche traitée. Elle est transmise à votre responsable pour appréciation.");
      setTreatId(null);
      setTreatNote("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTreatBusy(false);
    }
  };

  const reviewTask = async (t, review) => {
    if (review === "corriger" && !reviewComment.trim()) {
      return toast.error("Laissez un commentaire pour aider l'agent à corriger.");
    }
    setReviewBusy(true);
    try {
      await api.post(`/tasks/${t.id}/review`, { review, comment: reviewComment });
      toast.success(review === "parfait" ? "Appréciation transmise : parfait." : "Correction demandée à l'agent.");
      setReviewFor(null);
      setReviewComment("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <div className="space-y-9">
      {isManager && (
        <section data-testid="assign-task-section">
          <h2 className="font-display text-xl font-bold text-korban-navy">Assigner une tâche</h2>
          <form onSubmit={assignTask} data-testid="assign-task-form" className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="t-title" className="mb-1 block text-xs font-bold text-slate-600">Intitulé de la tâche</label>
                <input id="t-title" required minLength={3} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex. Préparer le point hebdomadaire RH" data-testid="task-input-title" className="field field-light" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-bold text-slate-600">Agent(s) destinataire(s) — un ou plusieurs</label>
                <div className="grid gap-2 sm:grid-cols-2" data-testid="task-agents-grid">
                  {candidates.map((p) => (
                    <label key={p.user_id} className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm font-semibold transition-all ${form.assignee_ids.includes(p.user_id) ? "border-korban-blue bg-korban-blue/10 text-korban-navy" : "border-slate-200 bg-white text-slate-600 hover:border-korban-blue/40"}`}>
                      <input
                        type="checkbox"
                        checked={form.assignee_ids.includes(p.user_id)}
                        onChange={() => toggleAssignee(p.user_id)}
                        data-testid={`task-check-${p.user_id}`}
                        className="h-4 w-4 accent-korban-blue"
                      />
                      {p.full_name}{p.user_id === user.user_id ? " — vous" : ""}
                      <span className="ml-auto text-[0.68rem] font-bold uppercase tracking-wider text-slate-400">{p.role}</span>
                    </label>
                  ))}
                  {candidates.length === 0 && (
                    <p className="text-xs text-slate-400">Aucun agent disponible dans votre département.</p>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="t-desc" className="mb-1 block text-xs font-bold text-slate-600">Description</label>
                <textarea id="t-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Détaillez la tâche à effectuer…" data-testid="task-input-description" className="field field-light resize-none" />
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="task-assign-button"
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-korban-navy px-6 py-3 text-sm font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Assigner la tâche
            </button>
          </form>

          <div className="mt-7">
            <h3 className="flex flex-wrap items-center gap-2 font-display text-lg font-bold text-korban-navy">
              Tâches que j'ai assignées
              {toReview > 0 && (
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700" data-testid="to-review-badge">
                  {toReview} en attente d'appréciation
                </span>
              )}
            </h3>
            {!tasks ? (
              <p className="mt-3 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
            ) : mine.length === 0 ? (
              <p className="mt-3 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="assigned-empty">
                Vous n'avez encore assigné aucune tâche.
              </p>
            ) : (
              <div className="mt-4 space-y-3" data-testid="assigned-list">
                {mine.map((t) => (
                  <TaskCard key={t.id} t={t}>
                    {t.status === "traitée" &&
                      (reviewFor === t.id ? (
                        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4" data-testid={`review-panel-${t.id}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-bold text-red-600">Commentaire pour aider l'agent</p>
                            <button onClick={() => setReviewFor(null)} data-testid={`review-cancel-${t.id}`} className="text-slate-400 hover:text-slate-600" aria-label="Annuler">
                              <X size={16} />
                            </button>
                          </div>
                          <textarea rows={3} value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
                            placeholder="Expliquez ce qui doit être corrigé…" data-testid={`review-comment-${t.id}`}
                            className="field field-light mt-3 resize-none text-sm" />
                          <button onClick={() => reviewTask(t, "corriger")} disabled={reviewBusy} data-testid={`review-confirm-${t.id}`}
                            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50">
                            {reviewBusy ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />}
                            Demander cette correction
                          </button>
                        </div>
                      ) : (
                        <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                          <button onClick={() => reviewTask(t, "parfait")} disabled={reviewBusy} data-testid={`task-parfait-${t.id}`}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-700 disabled:opacity-50">
                            {reviewBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Parfait
                          </button>
                          <button onClick={() => { setReviewFor(t.id); setReviewComment(""); }} disabled={reviewBusy} data-testid={`task-corriger-btn-${t.id}`}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-xs font-bold text-red-600 ring-1 ring-red-200 transition-all hover:-translate-y-0.5 hover:bg-red-50 disabled:opacity-50">
                            <AlertCircle size={13} /> À corriger
                          </button>
                        </div>
                      ))}
                    {t.review && t.status !== "traitée" && (
                      <p className="mt-3 border-t border-slate-100 pt-2 text-xs font-semibold text-slate-500" data-testid={`task-reviewed-${t.id}`}>
                        {t.review === "parfait" ? "Appréciation transmise : parfait." : "Correction demandée à l'agent."}
                      </p>
                    )}
                    {user.role === "gerant" && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Supprimer définitivement la tâche « ${t.title} » ?`)) return;
                          try {
                            await api.delete(`/tasks/${t.id}`);
                            toast.success("Tâche supprimée.");
                            load();
                          } catch (err) {
                            toast.error(formatApiErrorDetail(err.response?.data?.detail));
                          }
                        }}
                        data-testid={`task-delete-${t.id}`}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-xs font-bold text-red-600 ring-1 ring-red-200 transition-all hover:-translate-y-0.5 hover:bg-red-50"
                      >
                        <Trash2 size={13} /> Supprimer la tâche
                      </button>
                    )}
                  </TaskCard>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <section data-testid="received-tasks-section">
        <h2 className="flex flex-wrap items-center gap-2 font-display text-xl font-bold text-korban-navy">
          <ListTodo size={20} className="text-korban-blue" /> Tâches reçues
          {toTreat > 0 && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700" data-testid="to-treat-badge">
              {toTreat} à traiter
            </span>
          )}
        </h2>
        {!tasks ? (
          <p className="mt-3 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : received.length === 0 ? (
          <p className="mt-3 rounded-xl border border-dashed border-slate-300 p-5 text-center text-sm text-slate-500" data-testid="received-empty">
            Aucune tâche ne vous a été assignée pour l'instant.
          </p>
        ) : (
          <div className="mt-4 space-y-3" data-testid="received-list">
            {received.map((t) => (
              <TaskCard key={t.id} t={t}>
                {["assignée", "corriger"].includes(t.status) &&
                  (treatId === t.id ? (
                    <div className="mt-4 rounded-xl border border-korban-blue/30 bg-korban-blue/5 p-4" data-testid={`treat-panel-${t.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold text-korban-navy">Traiter cette tâche</p>
                        <button onClick={() => setTreatId(null)} data-testid={`treat-cancel-${t.id}`} className="text-slate-400 hover:text-slate-600" aria-label="Annuler">
                          <X size={16} />
                        </button>
                      </div>
                      {t.review === "corriger" && t.review_comment && (
                        <p className="mt-3 rounded-lg bg-red-50 p-3 text-xs font-semibold text-red-600">
                          Rappel du commentaire du responsable : {t.review_comment}
                        </p>
                      )}
                      <textarea rows={3} value={treatNote} onChange={(e) => setTreatNote(e.target.value)}
                        placeholder="Compte rendu de traitement (optionnel)" data-testid={`treat-note-${t.id}`}
                        className="field field-light mt-3 resize-none text-sm" />
                      <input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
                        data-testid={`treat-file-${t.id}`}
                        className="mt-3 block w-full text-xs text-slate-600 file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-korban-navy file:px-4 file:py-2 file:text-xs file:font-bold file:text-korban-gold" />
                      <button onClick={() => treatTask(t)} disabled={treatBusy} data-testid={`treat-confirm-${t.id}`}
                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-korban-navy px-5 py-2.5 text-xs font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2 disabled:cursor-not-allowed disabled:opacity-50">
                        {treatBusy ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
                        Transmettre au responsable
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => { setTreatId(t.id); setTreatNote(""); }} data-testid={`treat-button-${t.id}`}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-korban-navy px-4 py-2 text-xs font-bold text-korban-gold transition-all hover:-translate-y-0.5 hover:bg-korban-navy2">
                      <Clock size={13} /> Traiter cette tâche
                    </button>
                  ))}
              </TaskCard>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
